<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import Konva from "konva";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import {
  Cell,
  CellGroup,
  Popup,
  showConfirmDialog,
  showLoadingToast,
  showSuccessToast,
} from "vant";
import useMoveAndSelection from "@/components/mobile/generation/custom-painting/useMoveAndSelection";
import i18n from "@/locales";
import { useStoryGameEditor } from "@/stores/storyGameEditor";
import { useRoute, useRouter } from "vue-router";
import {
  AddImageToStageInstance,
  MediaImageShape,
} from "@/components/mobile/generation/custom-painting/AddImageToStage.vue";
import { exportImage } from "@/components/mobile/generation/util";
import { useMedia } from "@/stores/media";
import { useMate } from "@/stores/mate";
import api from "@/modules/api";
import { useI18n } from "vue-i18n";
import { useNodeTypeText } from "@/use/common";
import { DialogPreviewInstance } from "@/components/mobile/gal-game/editor/components/DialogPreview.vue";
import Stage = Konva.Stage;
import Layer = Konva.Layer;
import Image = Konva.Image;
import LightArrowIcon from "@/components/icon/LightArrowIcon.vue";
import AddAnimationToStage, {
  AddAnimationToStageInstance,
  AnimationImageShape,
} from "@/components/mobile/generation/animation/AddAnimationToStage.vue";
import { MateTraitAnimation } from "@/types/entity";
import { routerBack } from "@/use/go";

Konva.hitOnDragEnabled = true;
const PORTRAIT_ID_PREFIX = "portrait_";
const generatePortraitId = (id: number) => {
  return `${PORTRAIT_ID_PREFIX}${id}`;
};
const extractPortraitId = (id: string) => {
  return Number(id.replace(PORTRAIT_ID_PREFIX, ""));
};
const ANIMATION_ID_PREFIX = "animation_";
const generateAnimationId = (id: number) => {
  return `${ANIMATION_ID_PREFIX}${id}`;
};
const extractAnimationId = (id: string) => {
  return Number(id.replace(ANIMATION_ID_PREFIX, ""));
};
// Props

// State
const storyNodeStore = useStoryGameEditor();
const route = useRoute();
const router = useRouter();
const media = useMedia();
const mate = useMate();
const { t } = useI18n();

const showPopup = ref(false);
const showClearPopup = ref(false);
const showBatchApplyPopup = ref(false);
const dialogId = ref(Number(route.query.dialogId));
const nodeId = ref(Number(route.query.nodeId));
const storySceneId = ref(Number(route.query.storySceneId));
const currentNode = computed(() => storyNodeStore.nodeMap[nodeId.value]);
const currentDialog = computed(() => storyNodeStore.dialogMap[dialogId.value]);
const nodeList = computed(() =>
  storyNodeStore.nodes.map((n) => storyNodeStore.nodeMap[n])
);
const galGameId = Number(route.query.galGameId);
const previewDialogId = ref(Number(route.query.dialogId));
const previewNodeId = ref(Number(route.query.nodeId));
const main = ref<HTMLDivElement | null>(null);
const stage = ref<Stage | null>(null);
const backgroundLayer = ref<Layer | null>(null); // 背景图片的图层
const rolesLayer = ref<Layer | null>(null); // 立绘图片的图层
const characterShapes = ref<Image[]>([]);
const backgroundShapes = ref<Image[]>([]);
const animationShapes = ref<Image[]>([]);
const enableObjectMove = ref(true);
const bgImage = ref<AddImageToStageInstance>();
const roleImage = ref<AddImageToStageInstance>();
const animationRef = ref<AddAnimationToStageInstance | null>(null);
const toOperateDialogIdPair = ref<{ dialogId: number; nodeId: number }>({
  dialogId: 0,
  nodeId: 0,
});
const previewRef = ref<DialogPreviewInstance | null>(null);
const panelShapes = computed({
  get: () => [...animationShapes.value, ...characterShapes.value] as Image[],
  set: (shapes: Image[]) => {
    characterShapes.value = shapes.filter(
      (s) => !(s instanceof HTMLCanvasElement)
    );
    animationShapes.value = shapes.filter(
      (s) => s instanceof HTMLCanvasElement
    );
  },
});

const { clearSelection, activeShapes, transformer } = useMoveAndSelection(
  stage,
  rolesLayer,
  enableObjectMove
);

// Lifecycle
const prepareKonva = () => {
  // 1. 加载图片，根据图片和容器确定画布的宽高
  // 获取main的宽高
  const { width } = main.value!.getBoundingClientRect();
  // 2. 初始化Konva
  stage.value = new Konva.Stage({
    container: "container",
    width,
    height: (9 * width) / 16,
    draggable: false,
  });
  const bgLayer = new Konva.Layer();
  stage.value?.add(bgLayer);
  backgroundLayer.value = bgLayer;
  const characterLayer = new Konva.Layer();
  stage.value?.add(characterLayer);
  rolesLayer.value = characterLayer;
};
const recoveryState = async (targetDialogId: number, inherit: boolean) => {
  if (!targetDialogId) return;
  let targetDialog = await storyNodeStore.getOrFetchDialog(targetDialogId);
  // 1. 当前 dialog 场景和立绘为空，尝试使用上次编辑的 dialog 的场景和立绘
  if (inherit) {
    const prevDialogId = storyNodeStore.lastDialogIdEditedPerformance;
    if (
      prevDialogId &&
      targetDialog?.sceneMedia &&
      targetDialog?.portraitzz?.length === 0 &&
      targetDialog?.animationzz?.length === 0
    ) {
      // targetDialog = await storyNodeStore.getOrFetchDialog(prevDialogId);
    }
  }
  const promises: Promise<void>[] = [];
  // 2. 加载背景图片
  if (targetDialog.sceneMediaId) {
    promises.push(
      bgImage.value!.addImage(targetDialog.sceneMedia?.url, {
        transform: targetDialog.sceneMediaInfo,
        mediaId: targetDialog.sceneMediaId!,
      })
    );
  }
  const shouldClearId = targetDialog.id !== dialogId.value;
  // 3. 加载立绘图片
  const roles = targetDialog.portraitzz ?? [];
  for (const role of roles)
    promises.push(
      roleImage.value!.addImage(role.media.url, {
        transform: role.mediaInfo,
        mediaId: role.mediaId!,
        id: shouldClearId ? undefined : generatePortraitId(role.id!),
      })
    );
  // 4. 加载动画立绘
  const animationList: StoryGameAnimation[] = targetDialog.animationzz ?? [];
  for (const animation of animationList) {
    const traitAnimation: MateTraitAnimation = {
      ...animation.animation,
      portraitzz: animation.animation.portraitzz.map((p) => ({
        portrait: p,
        type: p.pivot.type,
      })),
    };
    promises.push(
      animationRef.value!.addAnimation(traitAnimation, {
        transform: animation.mediaInfo,
        mateTraitAnimationId: animation.animationId!,
        id: shouldClearId ? undefined : generateAnimationId(animation.id!),
      })
    );
  }
  await Promise.all(promises);
};

onMounted(async () => {
  const loading = showLoadingToast({
    message: i18n.global.t("Message.loading"),
  });
  try {
    prepareKonva();
    if (dialogId.value) await recoveryState(dialogId.value, true);
    if (storySceneId.value) {
      const { url } = route.query;
      if (url) await bgImage.value!.addImage(url as string);
    }
  } finally {
    loading.close();
  }
});

onBeforeUnmount(() => {
  if (dialogId.value)
    storyNodeStore.lastDialogIdEditedPerformance = dialogId.value;
  resetStage();
});

watch([dialogId, nodeId], ([dialogId, nodeId]) => {
  router.replace({
    query: {
      galGameId: galGameId.toString(),
      dialogId: dialogId.toString(),
      nodeId: nodeId.toString(),
    },
  });
});

// Event Handler
const resetStage = () => {
  stage.value?.destroy();
  stage.value = null;
  rolesLayer.value = null;
  characterShapes.value = [];
};

const clear = async (force = false, mode: "bg" | "char" | "all" = "all") => {
  try {
    if (!force && mode === "all") {
      await showConfirmDialog({
        title: i18n.global.t("ConfirmModal.promptTitle"),
        message: i18n.global.t("ConfirmModal.clearTheCanvas"),
        confirmButtonText: i18n.global.t("Buttons.confirm"),
        cancelButtonText: i18n.global.t("Buttons.cancel"),
      });
    }
    if (mode === "all" || mode === "bg") {
      backgroundShapes.value.forEach((s) => s.remove());
    }
    if (mode === "all" || mode === "char") {
      characterShapes.value.forEach((s) => s.remove());
      characterShapes.value = [];
      animationShapes.value.forEach((s) => s.remove());
      animationShapes.value = [];
    }
    clearSelection();
  } catch {
    // do nothing
  } finally {
    showClearPopup.value = false;
  }
};

const buildTransformInfo = (s: Konva.Image) => {
  const imageWidth = s.attrs.image.width;
  const imageHeight = s.attrs.image.height;
  const originX = stage.value!.width() / 2;
  const originY = stage.value!.height() / 2;
  const transformOffsetScaleX = 1920 / stage.value!.width();
  const transformOffsetScaleY = 1080 / stage.value!.height();
  const transformScale =
    s.scaleX() /
    Math.min(
      stage.value!.width() / imageWidth,
      stage.value!.height() / imageHeight
    );

  return {
    scale: s.scaleX() * s.scaleY() !== 1 ? s.scale() : undefined,
    rotate: s.rotation() !== 0 ? s.rotation() : undefined,
    position: {
      x: s.x(),
      y: s.y(),
    },
    transform: {
      position: {
        x: (s.x() - originX) * transformOffsetScaleX,
        y: (s.y() - originY) * transformOffsetScaleY,
      },
      scale: {
        x: transformScale,
        y: transformScale,
      },
      rotation: s.rotation() !== 0 ? (s.rotation() * Math.PI) / 180 : undefined,
    },
    zIndex: s.zIndex(),
  } as MediaTransform;
};

const exportPreview = async () => {
  const targetWidth = 188;
  const targetHeight = 106;
  const file = exportImage(
    stage.value as Konva.Stage,
    targetWidth,
    targetHeight
  );
  const { item } = await api.uploadMedia(11, file, mate.currentMate!.id);
  return item;
};

const onSaveAndGeneratePreview = async (targetDialogId: number) => {
  const loading = showLoadingToast({
    message: i18n.global.t("Message.saving"),
  });
  try {
    const backgroundImage = backgroundLayer
      .value!.find((s: Konva.Node) => s instanceof Konva.Image)
      .map((s) => ({ mediaId: s.attrs.mediaId }));
    const allImageNodes: Image[] = rolesLayer.value!.find(
      (s: Konva.Node) => s instanceof Konva.Image
    );
    const roleImages = allImageNodes
      .filter((s) => s.image() instanceof HTMLImageElement)
      .map((s) => {
        const shape = s as MediaImageShape;
        return {
          id: shape.id(),
          mediaId: shape.attrs.mediaId,
          mediaTransform: buildTransformInfo(shape),
        };
      });
    const animationImages = allImageNodes
      .filter((s) => s.image() instanceof HTMLCanvasElement)
      .map((s) => {
        const shape = s as AnimationImageShape;
        return {
          id: shape.id(),
          animationId: shape.attrs.mateTraitAnimationId,
          mediaTransform: buildTransformInfo(shape),
        };
      });

    const promises: Promise<any>[] = [];
    // 立绘的CRUD（不能加非空判断，可能是删除）
    const roles = roleImages.map((r) => {
      return {
        id:
          r.id && targetDialogId === dialogId.value
            ? extractPortraitId(r.id)
            : undefined,
        mediaId: r.mediaId,
        mediaInfo: r.mediaTransform,
      };
    });
    promises.push(storyNodeStore.setDialogPortrait(targetDialogId, roles));
    // 动画的CRUD（不能加非空判断，可能是删除）
    const animations = animationImages.map((r) => {
      return {
        id:
          r.id && targetDialogId === dialogId.value
            ? extractAnimationId(r.id)
            : undefined,
        animationId: r.animationId,
        mediaInfo: r.mediaTransform,
      };
    });
    promises.push(
      storyNodeStore.setDialogAnimation(targetDialogId, animations)
    );
    // 预览图 & 背景图的更新
    const bgImg = backgroundImage[0];
    const getPreviewMediaId = async () =>
      backgroundImage.length || roleImages.length
        ? (await exportPreview()).id
        : 0;
    promises.push(
      getPreviewMediaId().then((mediaId) =>
        storyNodeStore.updateDialogImg(targetDialogId, {
          sceneMediaId: bgImg?.mediaId ?? 0,
          previewMediaId: mediaId,
        })
      )
    );
    await Promise.all(promises);
    loading.close();
    showSuccessToast(i18n.global.t("Message.saveSuccess"));
  } finally {
    loading.close();
  }
};
const goBack = () => {
  storyNodeStore.isInitDialog = false;
  routerBack();
};
const onSwitch = async () => {
  let loading = null;
  try {
    await showConfirmDialog({
      title: i18n.global.t("ConfirmModal.sureSwitchTitle"),
      message: i18n.global.t("ConfirmModal.sureSwitchDesc"),
      confirmButtonText: i18n.global.t("Buttons.confirm"),
      cancelButtonText: i18n.global.t("Buttons.cancel"),
    });
    await onSaveAndGeneratePreview(dialogId.value);
    loading = showLoadingToast({
      message: i18n.global.t("Message.loading"),
    });
    dialogId.value = toOperateDialogIdPair.value.dialogId;
    nodeId.value = toOperateDialogIdPair.value.nodeId;
    await clear(true);
    await recoveryState(dialogId.value, false);
  } finally {
    loading && loading.close();
  }
};
const onOverride = async () => {
  let loading = null;
  try {
    await showConfirmDialog({
      title: i18n.global.t("ConfirmModal.sureOverrideTitle"),
      message: i18n.global.t("ConfirmModal.sureOverrideDesc"),
      confirmButtonText: i18n.global.t("Buttons.confirm"),
      cancelButtonText: i18n.global.t("Buttons.cancel"),
    });
    loading = showLoadingToast({
      message: i18n.global.t("Message.loading"),
    });
    await clear(true);
    await recoveryState(toOperateDialogIdPair.value.dialogId, false);
  } finally {
    loading && loading.close();
  }
};
const onPreviewDialogClick = (dialogId: number, nodeId: number) => {
  toOperateDialogIdPair.value = { dialogId, nodeId };
  showPopup.value = true;
};
const onBatchOverride = async (dialogIds: number[]) => {
  let loading = null;
  try {
    await showConfirmDialog({
      title: t("ConfirmModal.sureBatchOverrideTitle"),
      message: t("ConfirmModal.sureBatchOverrideDesc"),
      confirmButtonText: i18n.global.t("Buttons.confirm"),
      cancelButtonText: i18n.global.t("Buttons.cancel"),
    });
    loading = showLoadingToast({
      message: i18n.global.t("Message.loading"),
    });
    const promises: Promise<any>[] = [];
    dialogIds.forEach((dialogId) => {
      promises.push(onSaveAndGeneratePreview(dialogId));
    });
    await Promise.all(promises);
    loading.close();
    showSuccessToast(t("Message.saveSuccess"));
    showBatchApplyPopup.value = false;
  } finally {
    loading && loading.close();
  }
};

const editingNodeText = computed(() => {
  const idx = nodeList.value.findIndex((n) => n.id === nodeId.value);
  const { name, type } = currentNode.value;
  return `${idx + 1}. ${useNodeTypeText(type)}: ${
    name ? name : t("game.unnamedNode")
  }`;
});
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr_auto] overflow-hidden">
    <threads-nav :go-back="goBack" :show-center="false" show-back>
      <template #left>
        <button class="btn btn-ghost" @click="goBack">
          <light-arrow-icon />
        </button>
        <div
          v-if="currentNode && currentDialog"
          class="flex flex-col flex-grow overflow-hidden text-xs md:text-sm"
        >
          <div class="van-ellipsis">
            <span class="text-base-content/50">{{ $t("Texts.node") }}：</span
            >{{ editingNodeText }}
          </div>
          <div class="van-ellipsis">
            <span class="text-base-content/50"
              >{{ $t(`SidebarNavi.conversation`) }}：</span
            >{{ currentDialog.text }}
          </div>
        </div>
      </template>
      <template #right>
        <button
          class="btn btn-xs md:btn-sm rounded"
          @click="showBatchApplyPopup = true"
        >
          {{ $t("Buttons.batchApply") }}
        </button>
        <button
          class="btn btn-xs md:btn-sm rounded btn-primary"
          @click="onSaveAndGeneratePreview(dialogId)"
        >
          {{ $t("Buttons.apply") }}
        </button>
      </template>
    </threads-nav>
    <div
      ref="main"
      class="overflow-hidden bg-neutral relative flex flex-col items-center justify-center"
    >
      <story-dialog-preview
        ref="previewRef"
        v-model:node-id="previewNodeId"
        :dialog-id="dialogId"
        :story-game-id="galGameId"
        class="absolute top-0 left-0 right-0 overflow-hidden"
        @onDialogClick="onPreviewDialogClick"
      />
      <div id="container" class="bg-white"></div>
      <div
        class="flex gap-2 p-2 w-full items-center justify-center bg-base-100"
      >
        <add-image-to-stage
          v-if="stage && backgroundLayer"
          ref="bgImage"
          v-model:imageShapes="backgroundShapes"
          :draggable="false"
          :layer="backgroundLayer"
          :media-select-opt="{
            replace: true,
            enableCrop: true,
            cropAspectRatio: 16 / 9,
          }"
          :stage="stage"
          full-screen
        >
          <button class="btn btn-xs md:btn-sm rounded">
            <pic-icon class="icon-sm hidden md:inline-block" />
            {{
              backgroundShapes.length === 0
                ? $t("Buttons.selectBackgroundImage")
                : $t("Buttons.changeBackgroundImage")
            }}
          </button>
        </add-image-to-stage>
        <add-image-to-stage
          v-if="stage && rolesLayer"
          ref="roleImage"
          v-model:imageShapes="characterShapes"
          :layer="rolesLayer"
          :media-select-opt="{
            maxCount: 5,
            enablePortraitGallery: true,
            enableImageGenGallery: false,
            enableMateGallery: false,
          }"
          :stage="stage"
        >
          <button class="btn btn-xs md:btn-sm rounded">
            <i
              class="iconfont icon-image-portrait hidden md:inline-block text-sm"
            />
            {{ $t("Buttons.selectCharacterImage") }}
          </button>
        </add-image-to-stage>
        <add-animation-to-stage
          v-if="stage && rolesLayer"
          ref="animationRef"
          v-model:imageShapes="animationShapes"
          :layer="rolesLayer"
          :stage="stage"
        >
          <button
            :disabled="animationShapes.length != 0"
            class="btn btn-xs md:btn-sm"
          >
            <i class="iconfont icon-movieend hidden md:inline-block text-sm" />
            {{ $t("Buttons.insertAnimationVerticalDrawing") }}
          </button>
        </add-animation-to-stage>
        <button
          v-show="characterShapes.length > 0 || backgroundShapes.length > 0"
          class="btn btn-xs md:btn-sm rounded"
          @click="showClearPopup = true"
        >
          <delete-icon class="hidden md:inline-block icon-sm" />
          {{ $t("Buttons.clear") }}
        </button>
      </div>
      <stage-operation-panel
        v-if="stage && rolesLayer"
        v-model:active-shapes="activeShapes"
        v-model:shapes="panelShapes"
        :layer="rolesLayer"
        :stage="stage"
        :title="`${$t('Texts.characterImage')} (${panelShapes.length})`"
        :transformer="transformer"
        class="absolute bg-base-100 bottom-0 left-0 w-[20rem] h-40"
      />
    </div>
    <popup
      v-model:show="showClearPopup"
      class="py-4 flex flex-col gap-2"
      position="bottom"
      round
    >
      <cell-group inset>
        <cell
          :title="$t('Buttons.clearBg')"
          clickable
          @click="clear(false, 'bg')"
        />
        <cell
          :title="$t('Buttons.clearCharacterAndAnimation')"
          clickable
          @click="clear(false, 'char')"
        />
        <cell
          :title="$t('Buttons.clearCanvas')"
          clickable
          @click="clear(false, 'all')"
        />
      </cell-group>
      <cell-group inset>
        <cell
          :title="$t('Buttons.cancel')"
          clickable
          @click="showClearPopup = false"
        ></cell>
      </cell-group>
    </popup>
    <popup v-model:show="showPopup" position="bottom" round>
      <div class="py-4 flex flex-col gap-2">
        <cell-group inset>
          <cell
            clickable
            @click="
              onSwitch();
              showPopup = false;
            "
          >
            <template #title>
              <div class="flex items-center gap-2">
                <edit-icon class="icon-sm" />
                <div>{{ t("game.base.edit") }}</div>
              </div>
            </template>
          </cell>
          <cell
            clickable
            @click="
              onOverride();
              showPopup = false;
            "
          >
            <template #title>
              <div class="flex items-center gap-2">
                <copy-icon class="icon-sm" />
                <div>{{ t("game.copyToCurrentCanvas") }}</div>
              </div>
            </template>
          </cell>
        </cell-group>
        <cell-group inset>
          <cell
            :title="t('Buttons.cancel')"
            clickable
            @click="showPopup = false"
          />
        </cell-group>
      </div>
    </popup>
    <StoryDialogSelectionPopup
      v-model:show="showBatchApplyPopup"
      :initial-node-id="nodeId"
      @confirm="onBatchOverride"
    />
  </div>
</template>
